import React, { useEffect, useState } from 'react';
import { RefreshCw, Search, Wallet } from 'lucide-react';
import { AdminPagination } from '../components/admin/AdminPagination';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { adminService } from '../services/adminService';
import { getApiErrorMessage } from '../utils/apiError';
import { formatMoney } from '../utils/formatMoney';

const PAGE_SIZE = 50;
const inputClass =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-red';

function formatDate(value) {
  return value
    ? new Date(value).toLocaleString('th-TH', {
        dateStyle: 'short',
        timeStyle: 'medium',
      })
    : '-';
}

export function AdminWalletPage() {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [draft, setDraft] = useState({
    search: '',
    refType: '',
    direction: '',
    dateFrom: '',
    dateTo: '',
  });
  const [filters, setFilters] = useState(draft);
  const [offset, setOffset] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      adminService.getWallet(),
      adminService.getTransactions({
        ...filters,
        limit: PAGE_SIZE,
        offset,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo ? `${filters.dateTo}T23:59:59` : undefined,
      }),
    ])
      .then(([walletData, txData]) => {
        if (!cancelled) {
          setWallet(walletData);
          setTransactions(txData);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getApiErrorMessage(err, 'โหลดข้อมูลไม่สำเร็จ'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, offset, refreshKey]);

  const applyFilters = (event) => {
    event.preventDefault();
    setOffset(0);
    setFilters({ ...draft });
  };
  const total = transactions?.total || 0;

  return (
    <div className="space-y-6">
      {error ? (
        <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-brand-red">
          {error}
        </p>
      ) : null}

      <Card className="border-2 border-brand-red/20">
        <CardHeader className="flex flex-row items-center justify-between bg-red-50/40">
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            ยอดคงเหลือ Admin
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            disabled={loading}
            onClick={() => setRefreshKey((key) => key + 1)}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            รีเฟรช
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-3xl font-bold text-brand-red">
            {wallet ? formatMoney(wallet.balance) : loading ? '...' : '-'}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            สรุปเงินเข้า–ออกทั้งระบบดูที่หน้า Dashboard
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>ประวัติ Transaction กระเป๋า Admin</CardTitle>
          <form className="mt-4 grid gap-3 md:grid-cols-6" onSubmit={applyFilters}>
            <input
              className={`${inputClass} md:col-span-2`}
              placeholder="ค้นหาประเภท / ref / หมายเหตุ"
              value={draft.search}
              onChange={(e) => setDraft({ ...draft, search: e.target.value })}
            />
            <input
              className={inputClass}
              placeholder="ประเภท เช่น ticket"
              value={draft.refType}
              onChange={(e) => setDraft({ ...draft, refType: e.target.value })}
            />
            <select
              className={inputClass}
              value={draft.direction}
              onChange={(e) => setDraft({ ...draft, direction: e.target.value })}
            >
              <option value="">เข้าและออก</option>
              <option value="in">เงินเข้า</option>
              <option value="out">เงินออก</option>
            </select>
            <input
              type="date"
              className={inputClass}
              value={draft.dateFrom}
              onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value })}
            />
            <div className="flex gap-2">
              <input
                type="date"
                className={`${inputClass} min-w-0 flex-1`}
                value={draft.dateTo}
                onChange={(e) => setDraft({ ...draft, dateTo: e.target.value })}
              />
              <Button type="submit" size="sm">
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <p className="mb-3 text-sm text-gray-500">รวม {total} รายการ</p>
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-red-100 text-gray-500">
                <th className="px-2 py-2">เวลา</th>
                <th className="px-2 py-2">ประเภท / Ref</th>
                <th className="px-2 py-2">เข้า</th>
                <th className="px-2 py-2">ออก</th>
                <th className="px-2 py-2">หมายเหตุ</th>
              </tr>
            </thead>
            <tbody>
              {(transactions?.items || []).map((row) => (
                <tr key={row.id} className="border-b border-gray-100">
                  <td className="whitespace-nowrap px-2 py-2 text-gray-600">
                    {formatDate(row.createdAt)}
                  </td>
                  <td className="px-2 py-2 font-mono text-xs text-brand-red">
                    {row.refType || '-'}
                    {row.refId ? ` #${row.refId}` : ''}
                  </td>
                  <td className="px-2 py-2 text-green-700">
                    {row.inAmount > 0 ? formatMoney(row.inAmount) : '-'}
                  </td>
                  <td className="px-2 py-2 text-brand-red">
                    {row.outAmount > 0 ? formatMoney(row.outAmount) : '-'}
                  </td>
                  <td className="max-w-xs truncate px-2 py-2 text-gray-600">
                    {row.note || '-'}
                  </td>
                </tr>
              ))}
              {!loading && !(transactions?.items || []).length ? (
                <tr>
                  <td colSpan={5} className="px-2 py-8 text-center text-gray-500">
                    ไม่พบรายการ
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          <AdminPagination
            offset={offset}
            limit={PAGE_SIZE}
            total={total}
            loading={loading}
            onOffsetChange={setOffset}
          />
        </CardContent>
      </Card>
    </div>
  );
}
